import { Component, computed, inject, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyDataCollectionService } from '../../../../../services/companies/company-data-collection.service';
import { CompanyDataCollectionDto, CompanyDataCollectionReportDto } from '../../../../../models/api/companyDataCollectionDto';
import { CompanyDataCollectionReportCategory } from '../../../../../models/enums/companyDataCollectionReportCategory.enum';
import { ReportType } from '../../../../../models/enums/reportType.enum';
import { UpsertDataCollectionRequest } from '../../../../../models/api/upsertDataCollectionRequest';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-collection-tab',
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective
  ],
  templateUrl: './collection-tab.component.html',
  styleUrl: './collection-tab.component.scss'
})
export class CollectionTabComponent implements OnInit, HasUnsavedChanges {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private dataCollectionService = inject(CompanyDataCollectionService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Collection));

  readonly ReportType = ReportType;
  readonly CompanyDataCollectionReportCategory = CompanyDataCollectionReportCategory;

  readonly maxTypeBits = 5;

  readonly reportTypeFlagsMap: { [key: string]: { bit: number; label: string }[] } = {
    '1_2': [
      { bit: 0, label: 'Annual Report / Reference Document / Integrated Report / Universal Registration Document' },
      { bit: 1, label: 'Proxy Statement / Proxy Circular' },
      { bit: 2, label: '20-F' }
    ],
    '1_3': [
      { bit: 0, label: 'C.G. Report' },
      { bit: 1, label: 'CG Report included in A.R.' }
    ],
    '2_1': [
      { bit: 0, label: 'R.R. Report' },
      { bit: 1, label: 'Remuneration Report included in A.R.' }
    ],
    '3_1': [
      { bit: 0, label: 'Financial Report-Financial Statements' },
      { bit: 1, label: 'Audit Report included in A.R.' },
      { bit: 2, label: 'Auditor Reports' },
      { bit: 3, label: '10-K' },
      { bit: 4, label: 'Annual Information Form' }
    ],
    '4_1': [
      { bit: 0, label: 'ESG Reports' },
      { bit: 1, label: 'ESG Report included in A.R.' }
    ],
    '5_1': [
      { bit: 0, label: '8K / 6K / Report of Voting Results / Result of AGM' },
      { bit: 1, label: 'Notice of Annual General Meeting (Notice of AGM).' }
    ]
  };

  readonly months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  readonly reportTypes = [
    { value: ReportType.None, label: 'None' },
    { value: ReportType.Custom, label: 'Custom' }
  ];

  readonly categories = [
    { value: CompanyDataCollectionReportCategory.CorporateGovernance, label: 'Corporate Governance' },
    { value: CompanyDataCollectionReportCategory.Compensation, label: 'Compensation' },
    { value: CompanyDataCollectionReportCategory.Audit, label: 'Audit' },
    { value: CompanyDataCollectionReportCategory.Csr, label: 'ESG' },
    { value: CompanyDataCollectionReportCategory.VotingResults, label: 'Voting Results' }
  ];

  readonly leftCategories = [
    { value: CompanyDataCollectionReportCategory.CorporateGovernance, label: 'Corporate Governance' },
    { value: CompanyDataCollectionReportCategory.Compensation, label: 'Compensation' },
  ];

  readonly rightCategories = [
    { value: CompanyDataCollectionReportCategory.Audit, label: 'Audit' },
    { value: CompanyDataCollectionReportCategory.Csr, label: 'ESG' },
    { value: CompanyDataCollectionReportCategory.VotingResults, label: 'Voting Results' },
  ];

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  data: CompanyDataCollectionDto | null = null;

  form: FormGroup = this.fb.group({
    fiscalYearEndDay: [null as number | null],
    fiscalYearEndMonth: [null as number | null],
    terms: [false],
    comments: [''],
    reports: this.fb.array([])
  });

  get reportsArray(): FormArray {
    return this.form.get('reports') as FormArray;
  }

  getReportsByCategory(category: CompanyDataCollectionReportCategory): FormGroup[] {
    return (this.reportsArray.controls.filter(
      (ctrl) => ctrl.get('category')?.value === category
    ) as FormGroup[]).sort((a, b) => (a.get('urlType')?.value ?? 0) - (b.get('urlType')?.value ?? 0));
  }

  createReportFormGroup(report?: CompanyDataCollectionReportDto): FormGroup {
    const typeValue = report?.type ?? 0;
    const group: { [key: string]: any } = {
      id: [report?.id ?? null],
      category: [report?.category ?? CompanyDataCollectionReportCategory.None],
      url: [report?.url ?? ''],
      urlType: [report?.urlType ?? 0],
      day: [report?.day ?? null],
      month: [report?.month ?? null],
      type: [typeValue],
      typeCustom: [report?.typeCustom ?? '']
    };
    for (let bit = 0; bit < this.maxTypeBits; bit++) {
      group[`typeBit${bit}`] = [!!(typeValue & (1 << bit))];
    }
    return this.fb.group(group);
  }

  getTypeFlags(reportGroup: FormGroup): { bit: number; label: string }[] | null {
    const category = reportGroup.get('category')?.value;
    const urlType = reportGroup.get('urlType')?.value;
    return this.reportTypeFlagsMap[`${category}_${urlType}`] ?? null;
  }

  onTypeFlagChange(reportGroup: FormGroup): void {
    let type = 0;
    for (let bit = 0; bit < this.maxTypeBits; bit++) {
      if (reportGroup.get(`typeBit${bit}`)?.value) {
        type |= 1 << bit;
      }
    }
    reportGroup.get('type')?.setValue(type);
    this.form.markAsDirty();
  }

  addReport(category: CompanyDataCollectionReportCategory): void {
    const group = this.createReportFormGroup();
    group.get('category')?.setValue(category);
    this.reportsArray.push(group);
    this.form.markAsDirty();
  }

  removeReport(reportGroup: FormGroup): void {
    const index = this.reportsArray.controls.indexOf(reportGroup);
    if (index >= 0) {
      this.reportsArray.removeAt(index);
      this.form.markAsDirty();
    }
  }

  getUrlLabel(reportGroup: FormGroup): string {
    const category = reportGroup.get('category')?.value;
    const urlType = reportGroup.get('urlType')?.value;
    return Helpers.getReportUrlLabel(category, urlType);
  }

  openUrl(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  clearControl(control: AbstractControl | null): void {
    if (!control) return;
    control.setValue(null);
    control.markAsDirty();
    this.form.markAsDirty();
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('collection'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'collection', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'collection', year: this.selectedYear }));
    }

    this.loadData();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'collection', year }));
    this.loadData();
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  private performSave(): void {
    if (!this.companyId) return;

    const formValue = this.form.value;
    const request: UpsertDataCollectionRequest = {
      year: parseInt(this.selectedYear),
      terms: formValue.terms,
      comments: formValue.comments,
      fiscalYearEndDay: formValue.fiscalYearEndDay ?? undefined,
      fiscalYearEndMonth: formValue.fiscalYearEndMonth ?? undefined,
      reports: (formValue.reports ?? []).map((r: any) => ({
        category: r.category,
        url: r.url ?? '',
        urlType: r.urlType ?? 0,
        day: r.day ?? 0,
        month: r.month ?? 0,
        type: r.type ?? ReportType.None,
        typeCustom: r.typeCustom ?? ''
      }))
    };

    this.spinnerService.show();
    this.dataCollectionService.upsert(this.companyId, request).subscribe({
      next: (data) => {
        this.showYearWarning = false;
        this.data = data;
        this.patchForm(data);
        this.spinnerService.hide();
        this.toast.success('Data collection saved successfully');
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to save data collection');
        console.error('Save error:', err);
      }
    });
  }

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.dataCollectionService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        // Both 'present' (record explicitly entered for the year) and 'inherited'
        // (older record's validity period covers the year) populate the form. Only
        // 'absent' resets it.
        if (resource.state === 'absent' || resource.data == null) {
          this.data = null;
          this.form.patchValue({ fiscalYearEndDay: null, fiscalYearEndMonth: null, terms: false, comments: '' }, { emitEvent: false });
          this.reportsArray.clear({ emitEvent: false });
          this.initDefaultReports();
          this.form.markAsPristine();
          this.showYearWarning = true;
        } else {
          this.showYearWarning = false;
          this.data = resource.data;
          this.patchForm(resource.data);
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          // Entity not found - silently handle
        } else {
          this.toast.error('Error loading data collection', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
        }

        console.error('Data Collection error:', error);
      }
    });
  }

  private initDefaultReports(): void {
    const defaults: { category: CompanyDataCollectionReportCategory; urlType: number }[] = [
      { category: CompanyDataCollectionReportCategory.CorporateGovernance, urlType: 1 },
      { category: CompanyDataCollectionReportCategory.CorporateGovernance, urlType: 2 },
      { category: CompanyDataCollectionReportCategory.CorporateGovernance, urlType: 3 },
      { category: CompanyDataCollectionReportCategory.Compensation, urlType: 1 },
      { category: CompanyDataCollectionReportCategory.Audit, urlType: 1 },
      { category: CompanyDataCollectionReportCategory.Csr, urlType: 1 },
      { category: CompanyDataCollectionReportCategory.VotingResults, urlType: 1 },
    ];

    for (const def of defaults) {
      const group = this.createReportFormGroup();
      group.patchValue({ category: def.category, urlType: def.urlType }, { emitEvent: false });
      this.reportsArray.push(group, { emitEvent: false });
    }
  }

  private patchForm(data: CompanyDataCollectionDto): void {
    this.form.patchValue({
      fiscalYearEndDay: data.fiscalYearEndDay ?? null,
      fiscalYearEndMonth: data.fiscalYearEndMonth ?? null,
      terms: data.terms ?? false,
      comments: data.comments ?? ''
    }, { emitEvent: false });

    this.reportsArray.clear({ emitEvent: false });
    (data.reports ?? []).forEach(report => {
      this.reportsArray.push(this.createReportFormGroup(report), { emitEvent: false });
    });

    this.form.markAsPristine();
  }
}
